/**
 * Error Logging Service
 * Sends frontend errors to the backend logging API
 *
 * IMPORTANT: Errors that originate from our backend API are NOT re-logged
 * because they are already tracked in the AutoLedgerException system.
 */
import { ApiException } from "./api";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

/**
 * Error severity levels
 */
export type ErrorSeverity = "blocking" | "warning";

/**
 * Error log request payload
 */
export interface ErrorLogPayload {
    operation: string;
    errorMessage: string;
    stackTrace?: string;
    documentId?: number;
    fileStorageId?: number;
    organizationId?: number;
    metadata?: Record<string, unknown>;
    severity?: ErrorSeverity;
    category?: string;
}

/**
 * Response from the error logging endpoint
 */
export interface ErrorLogResponse {
    exceptionId: number;
    status: string;
    timestamp: string;
}

/**
 * Check if an error originated from our backend API
 * Backend errors have status > 0 (HTTP status codes)
 * These errors are already logged on the backend side
 */
export function isBackendError(error: unknown): boolean {
    if (error instanceof ApiException) {
        // ApiException with status > 0 means it came from our backend
        // status === 0 means it was a network error or other client-side issue
        return error.status > 0;
    }
    return false;
}

/**
 * Get auth token from Clerk if available
 * This is done without hooks so it can be used in non-React contexts
 */
async function getAuthTokenSafe(): Promise<string | null> {
    try {
        // Try to get the Clerk instance from the window
        // This may not be available immediately on page load
        const clerk = (window as unknown as { Clerk?: { session?: { getToken: (opts?: { template?: string }) => Promise<string | null> } } }).Clerk;
        if (clerk?.session) {
            return await clerk.session.getToken({ template: "jwt-template" });
        }
    } catch {
        // Silently fail - auth is optional for error logging
    }
    return null;
}

/**
 * Log an error to the backend
 *
 * @param payload - Error details to log
 * @param token - Optional auth token (if not provided, will try to get from Clerk)
 * @returns Response from the logging endpoint, or null if logging failed
 */
export async function logError(payload: ErrorLogPayload, token?: string | null): Promise<ErrorLogResponse | null> {
    try {
        // Get auth token if not provided
        const authToken = token ?? (await getAuthTokenSafe());

        const headers: Record<string, string> = {
            "Content-Type": "application/json",
        };

        if (authToken) {
            headers["Authorization"] = `Bearer ${authToken}`;
        }

        const response = await fetch(`${API_BASE_URL}/logs/frontend/error`, {
            method: "POST",
            headers,
            body: JSON.stringify({
                source: "frontend",
                operation: payload.operation,
                errorMessage: payload.errorMessage,
                stackTrace: payload.stackTrace,
                documentId: payload.documentId,
                fileStorageId: payload.fileStorageId,
                organizationId: payload.organizationId,
                metadata: payload.metadata ? JSON.stringify(payload.metadata) : undefined,
                severity: payload.severity || "warning",
                category: payload.category,
            }),
        });

        if (!response.ok) {
            console.error("[ErrorLogging] Failed to log error:", response.statusText);
            return null;
        }

        return await response.json();
    } catch (err) {
        // Fail silently to avoid infinite error loops
        console.error("[ErrorLogging] Error logging service failed:", err);
        return null;
    }
}

/**
 * Log an error from a caught exception
 * Automatically extracts error message and stack trace
 * Will NOT log errors that originated from our backend
 *
 * @param error - The caught error
 * @param operation - What operation was being attempted
 * @param additionalContext - Additional context to include in metadata
 * @returns Response from the logging endpoint, or null if logging failed/skipped
 */
export async function logCaughtError(
    error: unknown,
    operation: string,
    additionalContext?: {
        documentId?: number;
        fileStorageId?: number;
        metadata?: Record<string, unknown>;
        severity?: ErrorSeverity;
        category?: string;
    },
): Promise<ErrorLogResponse | null> {
    // Skip logging if this error came from our backend (already logged there)
    if (isBackendError(error)) {
        console.debug("[ErrorLogging] Skipping backend error (already logged on server):", error);
        return null;
    }

    let errorMessage = "Unknown error";
    let stackTrace: string | undefined;

    if (error instanceof Error) {
        errorMessage = error.message;
        stackTrace = error.stack;
    } else if (typeof error === "string") {
        errorMessage = error;
    } else {
        try {
            errorMessage = JSON.stringify(error);
        } catch {
            errorMessage = String(error);
        }
    }

    return logError({
        operation,
        errorMessage,
        stackTrace,
        documentId: additionalContext?.documentId,
        fileStorageId: additionalContext?.fileStorageId,
        metadata: {
            ...additionalContext?.metadata,
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
        },
        severity: additionalContext?.severity,
        category: additionalContext?.category,
    });
}

/**
 * Set up global error handlers for uncaught errors
 * Call this once during app initialization
 */
export function setupGlobalErrorHandlers(): void {
    // Handle uncaught errors
    window.onerror = (message, source, lineno, colno, error) => {
        // Skip backend errors
        if (error && isBackendError(error)) {
            return false;
        }

        logError({
            operation: "UncaughtError",
            errorMessage: typeof message === "string" ? message : "Unknown error",
            stackTrace: error?.stack,
            metadata: {
                source,
                lineno,
                colno,
                url: window.location.href,
                userAgent: navigator.userAgent,
            },
            severity: "blocking",
            category: "UncaughtError",
        });

        // Return false to allow the error to propagate to the console
        return false;
    };

    // Handle unhandled promise rejections
    window.onunhandledrejection = (event) => {
        const error = event.reason;

        // Skip backend errors
        if (isBackendError(error)) {
            return;
        }

        let errorMessage = "Unhandled promise rejection";
        let stackTrace: string | undefined;

        if (error instanceof Error) {
            errorMessage = error.message;
            stackTrace = error.stack;
        } else if (typeof error === "string") {
            errorMessage = error;
        } else {
            try {
                errorMessage = JSON.stringify(error);
            } catch {
                errorMessage = String(error);
            }
        }

        logError({
            operation: "UnhandledRejection",
            errorMessage,
            stackTrace,
            metadata: {
                url: window.location.href,
                userAgent: navigator.userAgent,
            },
            severity: "blocking",
            category: "UnhandledRejection",
        });
    };
}
